/**
 * The People list page's search params. All of them are written and read by
 * `usePeopleListPage` (nuqs); declared here so typed `Link`s can build
 * filtered views and deep links survive validation.
 */
export type PeopleListSearch = {
  /** Email search term (mutually exclusive with `filters` server-side). */
  email?: string;
  /** Base64-encoded condition filters. */
  filters?: string;
  /** 1-based page number. */
  page?: number;
  /** Analytics attribution for inbound links (e.g. `segment_people`); consumed after one load. */
  source?: string;
};

/**
 * The email search term is a string everywhere downstream, but the router
 * JSON-parses search values, so `?email=123` arrives as the number `123` and
 * `?email=true` as the boolean. Both are ordinary things to search a profile
 * list for, so take their string form. Anything else — null, an object, an
 * array — is not a search term and stays absent, which the page distinguishes
 * from an empty one.
 */
function searchTerm(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  return undefined;
}

/**
 * `validateSearch` replaces this route's search, so the incoming params are
 * spread to preserve global ones the page doesn't own (masq_account_id, …) and
 * only the ones it does are narrowed.
 */
// oxlint-disable-next-line local-rules/no-unsafe-dictionary-type -- validateSearch is handed unparsed search input, and the pass-through carries those same unvalidated values
export function validatePeopleListSearch(search: Record<string, unknown>): Record<string, unknown> & PeopleListSearch {
  return {
    ...search,
    email: searchTerm(search.email),
    filters: typeof search.filters === 'string' ? search.filters : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
    source: typeof search.source === 'string' ? search.source : undefined,
  };
}
